import { type FC } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Image from '@segment/matcha/components/Image'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import dataActivationPng2x from './assets/DataActivation@2x.png'
import dataActivationWebp from './assets/DataActivation.webp'
import dataActivationWebp2x from './assets/DataActivation@2x.webp'

const copy = {
  heading: `Data activation`,
  description: `Unlock growth with actionable insights from unified customer data.`,
}

const data = [
  {
    heading: `Unify every interaction across channels`,
    description: `Segment’s powerful identity resolution engine consolidates customer interaction histories from all data sources into a single, comprehensive profile.`,
  },
  {
    heading: `Empower your teams to drive personalization with profile data`,
    description: `Enable teams across your organization to self-serve profile data from the warehouse to run personalized marketing campaigns, create predictive ML models, and inform product development decisions.`,
  },
  {
    heading: `Fully interoperable with all major data warehouses`,
    description: `Segment’s Reverse ETL supports all major data warehouses, such as Snowflake, Databricks, BigQuery, Postgres, and Redshift. It’s never been easier to sync data from the warehouse to essential marketing, sales, product, and data tools in real-time.`,
  },
]

const button = {
  href: `/use-cases/activate-customer-data/`,
  text: `Activate Customer Data`,
}

const DataActivation: FC = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper id="data-activation">
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pLarge" tag="p">
            {copy.description}
          </CopyText.Description>
        </Section.Copy>

        <Section.Contents>
          <ColumnsWrapper>
            <ColumnTrack columns={1} overrides={{ md: 2 }}>
              <picture>
                <source type="image/webp" srcSet={`${dataActivationWebp}, ${dataActivationWebp2x} 2x`} />
                <StyledImage
                  width="1248"
                  height="1020"
                  src={dataActivationPng2x}
                  alt="Illustration: Data Activation"
                  loading="lazy"
                />
              </picture>

              <Content>
                {data.map(({ heading, description }) => (
                  <FeatureBoxBase key={heading} alignment="left">
                    <FeatureBox.Body>
                      <FeatureBox.Title>{heading}</FeatureBox.Title>
                      <FeatureBox.Text variant="pMedium">{description}</FeatureBox.Text>
                    </FeatureBox.Body>
                  </FeatureBoxBase>
                ))}

                <ButtonWrapper>
                  <ButtonText
                    variant="primary"
                    size="medium"
                    href={getAbsoluteUrl(`${button.href}`, pageConfig)}
                    iconEnd={ChevronRightIcon}
                  >
                    {button.text}
                  </ButtonText>
                </ButtonWrapper>
              </Content>
            </ColumnTrack>
          </ColumnsWrapper>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    gap: 0;
  }
`

const ColumnsWrapper = styled.div`
  margin-top: 50px;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    margin-top: 20px;
  }
`

const Content = styled.div`
  max-width: 460px;
  display: grid;
  align-content: center;
  gap: 24px;
  justify-self: center;
`

const StyledImage = styled(Image)`
  width: 100%;
  max-width: 600px;
`

const ButtonWrapper = styled.div``

export default DataActivation
